<script setup lang="ts">
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import type { LocaleObject } from "@nuxtjs/i18n";

const { t } = useI18n()

const props = defineProps<{
  locales: LocaleObject[]
  selectable: string[]
  selected: string | null
}>()

const emit = defineEmits<{
  (e: 'update:selected', value: string | null): void,
  (e: 'click:add', value: string): void,
  (e: 'click:remove', value: string): void,
}>()

const open = ref(false)

const selectable = computed(() => props.locales.filter(locale => props.selectable.includes(locale.code)))
const addable = computed(() => props.locales.filter(locale => !props.selectable.includes(locale.code)))
</script>

<template>
  <div class="flex flex-row gap-2">
    <template v-for="item in selectable" :key="item.code">
      <div
        :aria-selected="item.code === props.selected"
        class="border bg-white rounded aria-selected:border-primary aria-selected:bg-primary aria-selected:bg-opacity-10 data-[valid=false]:border-red-500"
      >
        <button
          type="button"
          class="p-2 pr-1 hover:text-primary-highlighted active:text-primary-selected aria-selected:text-primary"
          @click="emit('update:selected', item.code)"
          :title="t('jc.component.localeSelector.selectButton.title')"
        >
          {{ item.name }}
        </button>
        <button
          class="p-2 pl-1 hover:text-primary-highlighted active:text-primary-selected aria-selected:text-primary"
          @click="emit('click:remove', item.code)"
          :title="t('jc.component.localeSelector.removeButton.title', { locale: item.name })"
        >
          <span class="sr-only">{{ t('jc.component.localeSelector.removeButton.label', { locale: item.name }) }}</span>
          <FontAwesomeIcon fixed-width icon="xmark" />
        </button>
      </div>
    </template>

    <div class="relative inline-block text-left">
      <div>
        <button
          id="menu-button"
          class="p-2 border bg-white rounded hover:text-primary-highlighted active:text-primary-selected aria-selected:text-primary"
          @click="() => open = !open"
          v-click-outside="() => open = false"
          :title="t('jc.component.localeSelector.menuButton.title')"
        >
          <span class="sr-only">{{ t('jc.component.localeSelector.menuButton.label') }}</span>
          <FontAwesomeIcon
            fixed-width
            icon="plus"
          />
        </button>
      </div>
      <Transition name="dropdown">
        <div
          v-if="open"
          class="absolute right-0 z-20 mt-1 origin-top-right rounded-md bg-secondary-highlighted text-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
          role="menu"
          aria-orientation="vertical"
          aria-labelledby="menu-button"
          tabindex="-1"
        >
          <div class="py-1 rounded-md flex flex-col gap-2" role="none">
            <template v-for="item in addable" :key="item.code">
              <button
                class="px-4 py-1 hover:text-primary-highlighted active:text-primary-selected text-left"
                @click.prevent.stop="() => {
                  open = false
                  emit('click:add', item.code)
                }"
                :title="t('jc.component.localeSelector.addButton.title', { locale: item.name })"
              >
                {{ item.name }}
              </button>
            </template>
          </div>
        </div>
      </Transition>
    </div>
  </div>
</template>

<!--suppress CssUnusedSymbol -->
<style scoped lang="postcss">
.dropdown-enter-active {
  @apply transition ease-out duration-100
}

.dropdown-enter-from {
  @apply transform opacity-0 scale-95
}

.dropdown-enter-to {
  @apply transform opacity-100 scale-100
}

.dropdown-leave-active {
  @apply transition ease-in duration-75
}

.dropdown-leave-from {
  @apply transform opacity-100 scale-100
}

.dropdown-leave-to {
  @apply transform opacity-0 scale-95
}
</style>
